import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import {
  AdapterSource,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource,
  GenericObject,
  MhpDetails
} from 'modules/types/common';
import { CacheTypes } from 'modules/types/mobileCaching';
import { Dispatch } from 'redux';
import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import FileServices from 'services/FileServices';
import {
  medicationsDataSelector,
  medicationsDownloadingSelector,
  medicationsRequestStateSelector
} from 'store/medications/selectors';
import { messageRenewal, messageQuestion, messageShare } from 'store/messaging/actions';
import { currentConsumerPcpDoctorsSelector } from 'store/myDoctors/selectors';
import { eventListDataSelector } from 'store/events/selectors';
import { DFDHeaders } from 'serverTypes/authentication';
import { FormattedEvent } from 'serverTypes/event';
import { ProviderSummary } from 'store/findProvider/types';
import { RootDispatch, RootState, Thunk } from 'store/types';
import { oc } from 'ts-optchain';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { TransmitSources } from '../transmit';
import { Medication } from './types';

export const GET_MEDICATIONS_CACHE_SUCCESS = 'MEDICATIONS.GET_MEDICATIONS_CACHE_SUCCESS';

export const GET_MEDICATIONS = 'MEDICATIONS.GET_MEDICATIONS';
export const GET_MEDICATIONS_SUCCESS = 'MEDICATIONS.GET_MEDICATIONS_SUCCESS';
export const GET_MEDICATIONS_FAIL = 'MEDICATIONS.GET_MEDICATIONS_FAIL';

export const getMedications = (useRequestState = true) => ({
  type: GET_MEDICATIONS,
  payload: {
    useRequestState,
    requestStateSelector: medicationsRequestStateSelector,
    cacheType: CacheTypes.MEDICATIONS,
    cacheRetrievalSuccessAction: GET_MEDICATIONS_CACHE_SUCCESS,
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.MEDICATIONS
      )
    }
  }
});

export const GET_MEDICATION_DETAILS = 'MEDICATIONS.GET_MEDICATION_DETAILS';
export const GET_MEDICATION_DETAILS_SUCCESS = 'MEDICATIONS.GET_MEDICATION_DETAILS_SUCCESS';
export const GET_MEDICATION_DETAILS_FAIL = 'MEDICATIONS.GET_MEDICATION_DETAILS_FAIL';
export const GET_MEDICATION_DETAILS_RESET = 'MEDICATIONS.GET_MEDICATION_DETAILS_RESET';

export const getMedicationDetails = (id: string, mhpDetails: MhpDetails | GenericObject) => (
  dispatch: RootDispatch,
  getStore: () => RootState
) => {
  const state = getStore();
  const medications: Medication[] = medicationsDataSelector(state) || [];
  if (medications?.length) {
    const med = medications.find(m => m.id === id);
    if (med) {
      return dispatch({ type: GET_MEDICATION_DETAILS_SUCCESS, payload: { data: med } });
    }
  }
  const events: FormattedEvent[] = eventListDataSelector(state) || [];
  if (events?.length) {
    const event = events.find(e => e.sourceId === id && e.eventData);
    if (event) {
      return dispatch({ type: GET_MEDICATION_DETAILS_SUCCESS, payload: { data: event.eventData } });
    }
  }

  if (isEmpty(mhpDetails)) {
    // Probably arrived here from a pre-expansion bookmark, event, or deeplink.
    mhpDetails = {
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.MEDICATIONS,
        DispatcherEndpoint.ID
      ),
      headers: {
        [DFDHeaders.resourceId]: id
      }
    };
  }

  return dispatch({
    type: GET_MEDICATION_DETAILS,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'get',
        ...mhpDetails
      }
    }
  });
};

export const GET_MEDICATIONS_BY_ENCOUNTER_ID = 'MEDICATIONS.GET_MEDICATIONS_BY_ENCOUNTER_ID';
export const GET_MEDICATIONS_BY_ENCOUNTER_ID_SUCCESS =
  'MEDICATIONS.GET_MEDICATIONS_BY_ENCOUNTER_ID_SUCCESS';
export const GET_MEDICATIONS_BY_ENCOUNTER_ID_FAIL =
  'MEDICATIONS.GET_MEDICATIONS_BY_ENCOUNTER_ID_FAIL';

export const getMedicationsByEncounterId = (encounterId: string) => ({
  type: GET_MEDICATIONS_BY_ENCOUNTER_ID,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.MEDICATIONS
      ),
      headers: {
        [DFDHeaders.mhpFilterEncounterId]: encounterId
      }
    }
  }
});

export const createReplyBodyFromMedicationDetails = (medication: Medication) => {
  // Medication Information
  const prescribedDate = dayjs(medication.dateWritten).format('MM/DD/YYYY');
  const medicationName = medication.name;
  const dosageList = oc(medication).dosage([]);
  const dosageText = dosageList.map(
    dosage => `Dosage: ${dosage.dosage}\nInstructions: ${dosage.patientFriendlyInstructions}\n\n`
  );

  const replyBody = `Medication Name: ${medicationName}\n\nPrescribed Date: ${prescribedDate}\n\n${dosageText}`;
  return replyBody;
};

interface MedicationDetailsQuestionProps {
  medication: Medication;
  subject?: string;
  onSendSuccess?: () => void;
  config?: {
    recipient: ProviderSummary | null;
    lockRecipient?: boolean;
    lockSubject?: boolean;
    hideMessage?: boolean;
    onSend?: string; // If set, history will push to here
  };
  returnBackTo?: string;
}

interface MedicationsQuestionProps {
  medications: Medication[];
  subject?: string;
  onSendSuccess?: () => void;
}

export const askMedicationsQuestion = (
  props: MedicationsQuestionProps,
  isShare = false,
  returnBackTo?: string
): Thunk => (dispatch, getState) => {
  const consumerPcpList = currentConsumerPcpDoctorsSelector(getState());

  const messageParams = {
    subject: 'Medications Question',
    readonly: 'Question about my Medications',
    onSendSuccess: props.onSendSuccess,
    transmitParams: {
      source: TransmitSources.Medications,
      payload: props.medications,
      returnBackTo
    }
  };

  if (isShare) {
    dispatch(messageShare(messageParams));
  } else {
    dispatch(messageQuestion(messageParams, oc(consumerPcpList)[0].cernerPersonnelId('')));
  }
};

export const askMedicationRenewal = (
  props: MedicationDetailsQuestionProps
): Thunk => async dispatch => {
  const { medication, onSendSuccess, config, returnBackTo } = props;
  const replyBody = createReplyBodyFromMedicationDetails(medication);

  const params = {
    onSendSuccess,
    subject: 'Medication Renewal',
    readonly: replyBody,
    transmitParams: {
      source: TransmitSources.MedicationDetails,
      payload: medication.id,
      returnBackTo
    },
    ...config
  };

  dispatch(messageRenewal(params));
};

export const askMedicationDetailsQuestion = (
  props: MedicationDetailsQuestionProps
): Thunk => dispatch => {
  const {
    medication,
    onSendSuccess,
    config,
    returnBackTo,
    subject = 'Medication Question'
  } = props;

  const providerId = oc(medication).prescriber.reference('');
  const replyBody = createReplyBodyFromMedicationDetails(medication);

  const messageParams = {
    subject,
    onSendSuccess,
    readonly: replyBody,
    transmitParams: {
      source: TransmitSources.MedicationDetails,
      payload: { id: medication.id, adapterSource: medication.adapterSource },
      returnBackTo
    },
    ...config
  };

  dispatch(messageQuestion(messageParams, providerId));
};

/**
 * Share Medications
 */
export const SHARE_MEDICATIONS = 'MEDICATIONS.SHARE_MEDICATIONS';
export const SHARE_MEDICATIONS_SUCCESS = 'MEDICATIONS.SHARE_MEDICATIONS_SUCCESS';
export const SHARE_MEDICATIONS_FAIL = 'MEDICATIONS.SHARE_MEDICATIONS_FAIL';

export const shareMedications = (
  medications: any,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
) => ({
  type: SHARE_MEDICATIONS,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'post',
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.MEDICATIONS,
        DispatcherEndpoint.TRANSMIT
      ),
      data: {
        toAddress,
        message,
        useSecureEmail,
        contactPhoneNumber,
        subject
      }
    }
  }
});

export const SHARE_MEDICATION_DETAIL = 'MEDICATIONS.SHARE_MEDICATION_DETAIL';
export const SHARE_MEDICATION_DETAIL_SUCCESS = 'MEDICATIONS.SHARE_MEDICATION_DETAIL_SUCCESS';
export const SHARE_MEDICATION_DETAIL_FAIL = 'MEDICATIONS.SHARE_MEDICATION_DETAIL_FAIL';

interface ShareMedicationDetailPayload {
  id: string;
  adapterSource: string | undefined;
}

export const shareMedicationDetail = (
  { id: prescriptionId, adapterSource = AdapterSource.UTAH }: ShareMedicationDetailPayload,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
) => ({
  type: SHARE_MEDICATION_DETAIL,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'post',
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.MEDICATIONS,
        DispatcherEndpoint.ID_TRANSMIT
      ),
      headers: {
        [DFDHeaders.resourceId]: prescriptionId,
        [DFDHeaders.mhpAdapter]: adapterSource
      },
      data: {
        toAddress,
        message,
        useSecureEmail,
        contactPhoneNumber,
        subject
      }
    }
  }
});

/**
 * Download Medications
 */
export const DOWNLOAD_MEDICATIONS = 'MEDICATIONS.DOWNLOAD_MEDICATIONS';
export const DOWNLOAD_MEDICATIONS_SUCCESS = 'MEDICATIONS.DOWNLOAD_MEDICATIONS_SUCCESS';
export const DOWNLOAD_MEDICATIONS_FAIL = 'MEDICATIONS.DOWNLOAD_MEDICATIONS_FAIL';

export const downloadMedications = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();

  // Check for in flight downloads if there are any then return
  if (medicationsDownloadingSelector(state) === true) {
    return;
  }

  dispatch({ type: DOWNLOAD_MEDICATIONS, payload: {} });

  const timestamp = Date.now();

  try {
    const url = getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.MEDICATIONS,
      DispatcherEndpoint.DOWNLOAD,
      true
    );

    const response = await FileServices.download(`medications-${timestamp}.pdf`, url, {
      [DFDHeaders.mhpAccept]: 'application/pdf',
      ...getDownloadHeadersWithCurrentAccountConsumerId(state, url)
    });

    dispatch({ type: DOWNLOAD_MEDICATIONS_SUCCESS, payload: { response } });
  } catch (err) {
    dispatch({ type: DOWNLOAD_MEDICATIONS_FAIL, error: err });
  }
};
